import React, { useState } from 'react';
import { Bot, Play, CheckCircle, XCircle, Loader2, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/Button';
import { runSelfTests, formatReport } from '../lib/selfTest';
import type { TestReport } from '../lib/selfTest';
/**
 * AUTOMATED TESTING BOT (Admin → Tools).
 *
 * Runs the full self-test suite from lib/selfTest — store logic, real Supabase
 * round-trips, auth config and connectivity — then renders a grouped pass/fail
 * report with a copyable summary so any failures can be fixed quickly. The bot
 * cleans up every record it writes (sentinel-prefixed) when it finishes.
 */
export function TestBot() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
    current: string;
  }>({
    done: 0,
    total: 0,
    current: ''
  });
  const [report, setReport] = useState<TestReport | null>(null);
  const [copied, setCopied] = useState(false);
  const [showPassed, setShowPassed] = useState(false);
  const run = async () => {
    setRunning(true);
    setReport(null);
    setProgress({
      done: 0,
      total: 0,
      current: 'Starting…'
    });
    const result = await runSelfTests((done, total, current) => setProgress({
      done,
      total,
      current
    }));
    setReport(result);
    setRunning(false);
  };
  const copyReport = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(formatReport(report));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const groups = report ? Array.from(new Set(report.results.map(r => r.group))) : [];
  const pct = progress.total > 0 ? Math.round(progress.done / progress.total * 100) : 0;
  return <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-740" data-mp="23bg2fqtnlpv5">
      <h3 className="font-bold text-white mb-1 flex items-center" data-id="element-741" data-mp="1yimuku2w21eu">
        <Bot className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-742" data-mp="3sqqojlgpc2jq" />
        Automated Testing Bot
      </h3>
      <p className="text-sm text-gray-400 mb-4" data-id="element-743" data-mp="1liio9g1rqe4">
        Runs an end-to-end check of the platform — user flows &amp; store logic,
        live database read/write/delete, authentication and connectivity — and
        reports any failures. Test records are cleaned up automatically.
      </p>

      <Button onClick={run} isLoading={running} className="w-full" data-id="element-744" data-mp="3a126gsxy8gls">
        {!running && <Play className="w-4 h-4 mr-2" data-id="element-745" data-mp="3a355g2h2xk2n" />}
        {running ? 'Running tests…' : 'Run all tests'}
      </Button>

      {/* Live progress */}
      {running && <div className="mt-4" data-id="element-746" data-mp="3045xkm451sgp">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5" data-id="element-747" data-mp="304z9e1hyf85p">
            <span className="flex items-center" data-id="element-748" data-mp="353scczzm7grm">
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin text-brand-yellow" data-id="element-749" data-mp="yazul65mi8o9" />
              {progress.current}
            </span>
            <span className="font-mono" data-id="element-750" data-mp="139jj9eblpykb">
              {progress.done}/{progress.total || '…'}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-800" data-id="element-751" data-mp="2itaitd5z6owt">
            <div className="h-full rounded-full bg-brand-yellow transition-all duration-200" style={{
          width: `${pct}%`
        }} data-id="element-752" data-mp="2ir7jg2ixnjff" />
          </div>
        </div>}

      {/* Report */}
      {report && !running && <div className="mt-5" data-id="element-753" data-mp="36h1tkcbvatvz">
          {/* Summary */}
          <div className={`rounded-xl p-4 border mb-4 ${report.failed === 0 ? 'bg-green-900/30 border-green-500/50' : 'bg-red-900/30 border-red-500/50'}`} data-id="element-754" data-mp="32i1aqtte3r8z">
            <div className="flex items-center justify-between" data-id="element-755" data-mp="3bi7n2cdcql6k">
              <div className="flex items-center" data-id="element-756" data-mp="3bfv724r5mkw5">
                {report.failed === 0 ? <CheckCircle className="w-6 h-6 text-green-400 mr-3" data-id="element-757" data-mp="1m39u1e5lw8fi" /> : <XCircle className="w-6 h-6 text-red-400 mr-3" data-id="element-758" data-mp="1h5rjxkfurn5x" />}
                <div data-id="element-759" data-mp="1ikaq8wgzcy4s">
                  <p className={`font-bold ${report.failed === 0 ? 'text-green-300' : 'text-red-300'}`} data-id="element-760" data-mp="1in6oklwqpotr">
                    {report.failed === 0 ? 'All tests passed' : `${report.failed} test${report.failed === 1 ? '' : 's'} failed`}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5" data-id="element-761" data-mp="zv4g3xu95eul">
                    {report.passed}/{report.total} passed • {report.durationMs}
                    ms
                  </p>
                </div>
              </div>
              <button onClick={copyReport} className="flex items-center text-xs bg-gray-800 hover:bg-gray-700 text-gray-200 px-3 py-1.5 rounded-lg transition-colors" data-id="element-762" data-mp="19s0ehaqfsklf">
                {copied ? <>
                    <Check className="w-3.5 h-3.5 mr-1.5 text-green-400" data-id="element-763" data-mp="m2xwu2vsyrqx" />
                    Copied
                  </> : <>
                    <Copy className="w-3.5 h-3.5 mr-1.5" data-id="element-764" data-mp="2zbd5cppmaz14" />
                    Copy report
                  </>}
              </button>
            </div>
          </div>

          {/* Failures first (always visible), then passed (collapsible) */}
          {report.failed > 0 && <div className="space-y-4 mb-4" data-id="element-765" data-mp="2lfsmtvdswiu8">
              {groups.map(group => {
          const failures = report.results.filter(r => r.group === group && !r.passed);
          if (failures.length === 0) return null;
          return <GroupBlock key={group} title={group} results={failures} data-id="element-766" data-mp="27k74g722oxas" />;
        })}
            </div>}

          <button onClick={() => setShowPassed(s => !s)} className="flex items-center text-xs text-gray-400 hover:text-white transition-colors" data-id="element-767" data-mp="1xon7rifopukz">
            {showPassed ? <ChevronUp className="w-4 h-4 mr-1" data-id="element-768" data-mp="1j15ah54gc28l" /> : <ChevronDown className="w-4 h-4 mr-1" data-id="element-769" data-mp="1o0hvrlfod192" />}
            {showPassed ? 'Hide' : 'Show'} all {report.total} results
          </button>

          {showPassed && <div className="space-y-4 mt-3" data-id="element-770" data-mp="1a336qhtc2vhm">
              {groups.map(group => <GroupBlock key={group} title={group} results={report.results.filter(r => r.group === group)} data-id="element-771" data-mp="1e4emlvmeo2cm" />)}
            </div>}
        </div>}
    </div>;
}
function GroupBlock({
  title,
  results
}: {
  title: string;
  results: {
    name: string;
    passed: boolean;
    detail?: string;
  }[];
}) {
  return <div className="bg-gray-950 rounded-lg border border-gray-800 overflow-hidden" data-id="element-772" data-mp="2kqsbba8wxsx4">
      <div className="px-4 py-2 border-b border-gray-800 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-773" data-mp="2koo16akf7xoe">
        {title}
      </div>
      <div className="divide-y divide-gray-800/60" data-id="element-774" data-mp="e1qcwmpm53id">
        {results.map(r => <div key={r.name} className="flex items-start px-4 py-2.5 text-sm" data-id="element-775" data-mp="93ow68fntga2">
            {r.passed ? <CheckCircle className="w-4 h-4 mr-2.5 mt-0.5 text-green-400 shrink-0" data-id="element-776" data-mp="nsup5enwma9i" /> : <XCircle className="w-4 h-4 mr-2.5 mt-0.5 text-red-400 shrink-0" data-id="element-777" data-mp="isykdd7duesw" />}
            <div className="min-w-0" data-id="element-778" data-mp="3r8ie0dbytz8h">
              <p className="text-gray-200" data-id="element-779" data-mp="3r9jw31reflzp">{r.name}</p>
              {r.detail && <p className={`text-xs mt-0.5 break-words ${r.passed ? 'text-gray-500' : 'text-red-300/80'}`} data-id="element-780" data-mp="3mb8neucubhj2">
                  {r.detail}
                </p>}
            </div>
          </div>)}
      </div>
    </div>;
}